import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { ObjectTarget } from "../../app/permissions";
import { SurveyInfo } from "./surveySlice";

export interface SurveyListState {
    state: LoadStateInfo;
    request?: RetrieveSurveyListRequest;
    surveys: SurveyInfo[];
}

const initialState: SurveyListState = {
    state: notStarted(),
    surveys: [],
};

export interface RetrieveSurveyListRequest {
    target: ObjectTarget;
    targetId: string;
}

export interface RetrieveSurveyListByTargetsRequest {
    targets: ObjectTarget[];
}

// Retrieve surveys for a specific target and targetId
export const retrieveSurveyList = createAsyncThunk(
    "retrieveSurveyList",
    async (param: RetrieveSurveyListRequest, thunkAPI) => {
        console.debug("retrieve survey list", param);

        const response = await fetch(`${getBackendUrl()}/api/survey/list?target=${param.target}&targetId=${param.targetId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveSurveyListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.surveyList;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            if (status.state.state == LoadState.Loading) {
                return false;
            }
            // Allow reload if different parameters
            if (status.request && 
                (status.request.target !== param.target || status.request.targetId !== param.targetId)) {
                return true;
            }
            console.debug(`retrieveSurveyList skipped: ${status.state.state}`);
            return false;
        }
    }
);

// Retrieve surveys for multiple target types
export const retrieveSurveyListByTargets = createAsyncThunk(
    "retrieveSurveyListByTargets",
    async (param: RetrieveSurveyListByTargetsRequest, thunkAPI) => {
        console.debug("retrieve survey list by targets", param);

        const targetsParam = param.targets.join(',');
        const response = await fetch(`${getBackendUrl()}/api/survey/list/targets?targets=${targetsParam}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    }
);

export const surveyListSlice = createSlice({
    name: "surveyList",
    initialState,
    reducers: {
        setSurveys: (state, action: PayloadAction<SurveyInfo[]>) => {
            console.trace(`setSurveys :: ${action.payload.length} surveys`);
            state.surveys = action.payload;
        },
        addSurvey: (state, action: PayloadAction<SurveyInfo>) => {
            console.trace(`addSurvey :: ${action.payload.id}`);
            const existingIndex = state.surveys.findIndex(s => s.id === action.payload.id);
            if (existingIndex >= 0) {
                state.surveys[existingIndex] = action.payload;
            } else {
                state.surveys.push(action.payload);
            }
        },
        removeSurvey: (state, action: PayloadAction<string>) => {
            console.trace(`removeSurvey :: ${action.payload}`);
            state.surveys = state.surveys.filter(s => s.id !== action.payload);
        },
        updateSurvey: (state, action: PayloadAction<SurveyInfo>) => {
            console.trace(`updateSurvey :: ${action.payload.id}`);
            const existingIndex = state.surveys.findIndex(s => s.id === action.payload.id);
            if (existingIndex >= 0) {
                state.surveys[existingIndex] = action.payload;
            }
        },
        clearRequest: (state) => {
            state.request = undefined;
            state.state = notStarted();
        },
        clean: (state) => {
            state.request = undefined;
            state.state = notStarted();
            state.surveys = [];
        }
    },
    extraReducers: (builder) => {
        builder
            // retrieveSurveyList cases
            .addCase(retrieveSurveyList.pending, (state, action) => {
                state.request = action.meta.arg;
                state.state = loading();
            })
            .addCase(retrieveSurveyList.fulfilled, (state, action) => {
                console.debug(`${action.type}`, action.payload);
                state.state = loaded();
                state.surveys = action.payload;
            })
            .addCase(retrieveSurveyList.rejected, (state, action) => {
                state.state = error(action);
                state.surveys = [];
            })
            // retrieveSurveyListByTargets cases
            .addCase(retrieveSurveyListByTargets.pending, (state) => {
                state.request = undefined; // Different request type
                state.state = loading();
            })
            .addCase(retrieveSurveyListByTargets.fulfilled, (state, action) => {
                console.debug(`${action.type}`, action.payload);
                state.state = loaded();
                state.surveys = action.payload;
            })
            .addCase(retrieveSurveyListByTargets.rejected, (state, action) => {
                state.state = error(action);
                state.surveys = [];
            });
    },
});

export const { setSurveys, addSurvey, removeSurvey, updateSurvey, clearRequest, clean } = surveyListSlice.actions;

export const selectSurveyList = (state: RootState) => state.surveyList;

export const selectSurveyListSurveys = (state: RootState) => state.surveyList?.surveys || [];

export const selectSurveyListState = (state: RootState) => state.surveyList?.state || notStarted();

export default surveyListSlice.reducer;
